import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
// firebase/auth wird NICHT statisch importiert — sonst läge das komplette
// Auth-SDK (~114 KB) im Haupt-Bundle jeder öffentlichen Landingpage.
// getAuthModule() liefert dieselben Funktionen, nur nachgeladen.
// Siehe config/firebase.js für die Begründung und den Session-Marker.
import {
  getAuth,
  getAuthModule,
  hasPossibleSession,
  markSessionActive,
  markSessionChecked,
  clearSessionFlag,
  markRedirectPending,
  hasRedirectPending,
  clearRedirectPending,
} from '../config/firebase';
import { reportError } from '../components/common/ErrorBoundary';
import { usersService } from '../services';
import { claimPendingReferral } from '../services/referral.service';
import analytics from '../services/analytics.service';
import { applyTheme, getStoredPreference, subscribeSystemTheme, VALID_PREFERENCES } from '../utils/theme';

// Create Auth Context
const AuthContext = createContext(null);

/**
 * Auth Provider Component
 * Provides authentication state and methods to the app
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Ob dieser Browser eine Sitzung haben KÖNNTE — als State, nicht als
  // einmaliger Aufruf beim Mount. Der Marker in localStorage wird erst beim
  // Login gesetzt; ohne State-Update würde der onAuthStateChanged-Listener bei
  // einem Login auf einer frisch geladenen Seite nie angehängt, und der
  // erfolgreich eingeloggte User bliebe bis zum nächsten Reload ausgesperrt.
  const [sessionPossible, setSessionPossible] = useState(hasPossibleSession);

  // Marker setzen UND den Listener-Effekt aufwecken.
  const activateSession = useCallback(() => {
    markSessionActive();
    setSessionPossible(true);
  }, []);

  // Aktuelle Theme-Präferenz in einem Ref, damit der matchMedia-Listener
  // ohne Re-Subscribe immer den aktuellen Wert lesen kann.
  const themePreferenceRef = useRef(getStoredPreference());

  // Der rohe Firebase-User, gepflegt von onAuthStateChanged.
  // Nötig, seit Firebase nachgeladen wird: isPasswordUser() wird synchron im
  // Render von Settings.jsx aufgerufen und kann nicht auf getAuth() warten.
  const firebaseUserRef = useRef(null);

  const setThemePreference = useCallback((pref) => {
    const safe = VALID_PREFERENCES.includes(pref) ? pref : 'auto';
    themePreferenceRef.current = safe;
    applyTheme(safe);
  }, []);

  // Listener für OS-Theme-Wechsel. Wirkt nur, wenn Präferenz 'auto' ist.
  useEffect(() => {
    const unsubscribe = subscribeSystemTheme(
      () => themePreferenceRef.current,
      () => applyTheme('auto')
    );
    return unsubscribe;
  }, []);

  // Fetch user profile from backend
  const fetchUserProfile = useCallback(async () => {
    try {
      const response = await usersService.getMe();
      if (response.success) {
        return response.data;
      }
      return null;
    } catch (err) {
      console.error('Error fetching user profile:', err);
      return null;
    }
  }, []);

  // Beim Mount Redirect-Ergebnis abholen (Mobile-Google-Login).
  // Auf iOS/PWA wird signInWithRedirect verwendet – das Ergebnis liegt
  // nach Rückkehr aus dem Google-Flow vor und muss explizit konsumiert werden,
  // damit onAuthStateChanged den User picked.
  //
  // Nur wenn tatsächlich ein Redirect-Login gestartet wurde. Vorher lief der
  // Aufruf bei jedem Start mit möglicher Sitzung — auf iOS Safari scheitert er
  // dort ITP-bedingt und zeigte ein Fehlerbanner an, während der Popup-Login
  // daneben sauber durchlief. Kein Redirect gestartet → nichts abzuholen.
  useEffect(() => {
    if (!hasRedirectPending()) return;

    (async () => {
      try {
        const [authInstance, { getRedirectResult }] = await Promise.all([
          getAuth(),
          getAuthModule(),
        ]);
        await getRedirectResult(authInstance);
      } catch (err) {
        if (err?.code && err.code !== 'auth/no-auth-event') {
          logAuthError('google_redirect_result', err);
          setError(getErrorMessage(err.code));
        }
      } finally {
        clearRedirectPending();
      }
    })();
  }, []);

  // Listen for auth state changes.
  //
  // Ohne Session-Marker gibt es nichts zu beobachten: Der Nutzer gilt sofort als
  // ausgeloggt, loading endet, und Firebase bleibt ungeladen. Das ist der Fall,
  // der für die öffentlichen Seiten zählt.
  useEffect(() => {
    if (!sessionPossible) {
      setUser(null);
      setLoading(false);
      return undefined;
    }

    let unsubscribe = null;
    let cancelled = false;

    const attach = async () => {
      const [authInstance, { onAuthStateChanged }] = await Promise.all([
        getAuth(),
        getAuthModule(),
      ]);
      if (cancelled) return;

      unsubscribe = onAuthStateChanged(authInstance, async (firebaseUser) => {
      firebaseUserRef.current = firebaseUser;
      // Firebase hat geantwortet — ab jetzt ist der Marker maßgeblich und
      // dieser Browser lädt das SDK nicht mehr „auf Verdacht".
      markSessionChecked();
      try {
        if (firebaseUser) {
          // Sitzung bestätigt: Marker setzen. Deckt auch Nutzer ab, die schon
          // vor dem Lazy-Loading eingeloggt waren und den Marker nie bekamen.
          activateSession();
          // User is signed in - fetch profile from backend
          // The backend will automatically create the user if they don't exist
          const profile = await fetchUserProfile();
          
          if (profile) {
            setUser({
              ...profile,
              firebaseUid: firebaseUser.uid,
              emailVerified: firebaseUser.emailVerified
            });
            setThemePreference(profile.theme_preference || 'auto');
            // Creator-Referral: gespeicherten ?ref=-Code einlösen.
            // Fire-and-forget — das Backend ignoriert ungültige Codes und
            // Bestandsnutzer (Account älter als 7 Tage) still.
            claimPendingReferral();
          } else {
            // Backend couldn't fetch user - might be network issue
            // Set basic info from Firebase
            setUser({
              id: null,
              firebaseUid: firebaseUser.uid,
              email: firebaseUser.email,
              name: firebaseUser.displayName || 'User',
              username: null,
              avatar: firebaseUser.photoURL,
              role: 'both',
              level: 1,
              totalEarnings: 0,
              isNewUser: true
            });
          }
        } else {
          // User is signed out — der Marker war verwaist, also aufräumen,
          // damit der nächste Start Firebase gar nicht erst lädt.
          clearSessionFlag();
          setUser(null);
        }
      } catch (err) {
        console.error('Auth state change error:', err);
        setError(err.message);
        setUser(null);
      } finally {
        setLoading(false);
      }
      });
    };

    attach().catch((err) => {
      console.error('Firebase Auth konnte nicht geladen werden:', err);
      setLoading(false);
    });

    return () => {
      cancelled = true;
      if (unsubscribe) unsubscribe();
    };
  }, [sessionPossible, fetchUserProfile, setThemePreference, activateSession]);


  // Login with email/password
  const login = async (email, password) => {
    try {
      setError(null);
      setLoading(true);
      
      const [authInstance, { signInWithEmailAndPassword }] = await Promise.all([
        getAuth(),
        getAuthModule(),
      ]);
      const result = await signInWithEmailAndPassword(authInstance, email, password);
      activateSession();

      analytics.track('login', { method: 'password' });

      // Profile will be fetched automatically by onAuthStateChanged
      return { success: true, user: result.user };
    } catch (err) {
      logAuthError('password_login', err);
      const message = getErrorMessage(err.code);
      setError(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  };

  // Register with email/password
  const register = async (email, password, name) => {
    try {
      setError(null);
      setLoading(true);

      const [authInstance, authModule] = await Promise.all([getAuth(), getAuthModule()]);
      const result = await authModule.createUserWithEmailAndPassword(
        authInstance,
        email,
        password
      );
      activateSession();

      analytics.track('sign_up', { method: 'password' });

      // Update display name in Firebase
      if (name) {
        await authModule.updateProfile(result.user, { displayName: name });
        // Token-Refresh erzwingen, damit das Backend beim nächsten Request
        // den aktualisierten displayName im Token sieht (Race Condition Fix)
        await result.user.getIdToken(true);
        // Namen explizit ans Backend senden, falls onAuthStateChanged den User
        // bereits mit dem Email-Prefix als Name angelegt hat
        try {
          await usersService.updateProfile({ name });
        } catch {
          // Nicht-kritisch: Name kann später über die Profilseite gesetzt werden
        }
      }

      return { success: true, user: result.user };
    } catch (err) {
      const message = getErrorMessage(err.code);
      setError(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  };

  // Login with Google
  const loginWithGoogle = async () => {
    try {
      setError(null);
      setLoading(true);

      const [authInstance, authModule] = await Promise.all([getAuth(), getAuthModule()]);

      const provider = new authModule.GoogleAuthProvider();
      provider.setCustomParameters({
        prompt: 'select_account'
      });

      // Auf Mobile / installierter PWA funktioniert signInWithPopup nicht
      // zuverlässig: iOS Safari und Custom Tabs liefern das Ergebnis nicht
      // an window.opener zurück, der User landet nicht mehr in monemee.
      // → Redirect-Flow verwenden, Ergebnis wird beim Mount via
      // getRedirectResult abgeholt und onAuthStateChanged übernimmt den Rest.
      if (shouldUseRedirectFlow()) {
        // Beide Marker VOR dem Verlassen der Seite setzen — nach der Rückkehr
        // muss getRedirectResult laufen, und das passiert nur mit Marker.
        activateSession();
        markRedirectPending();
        await authModule.signInWithRedirect(authInstance, provider);
        // signInWithRedirect verlässt die Seite – kein Rückgabewert relevant.
        return { success: true, redirect: true };
      }

      const result = await authModule.signInWithPopup(authInstance, provider);
      activateSession();
      analytics.track('login', { method: 'google' });
      return { success: true, user: result.user };
    } catch (err) {
      logAuthError('google_login', err);
      const message = getErrorMessage(err.code);
      setError(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  };

  // Logout
  const logout = async () => {
    try {
      setLoading(true);
      const [authInstance, { signOut }] = await Promise.all([getAuth(), getAuthModule()]);
      await signOut(authInstance);
      clearSessionFlag();
      setUser(null);
      // Theme-Präferenz nach Logout auf 'auto' zurücksetzen
      setThemePreference('auto');
      return { success: true };
    } catch (err) {
      console.error('Logout error:', err);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  // Update user profile
  const updateProfile = async (data) => {
    try {
      setError(null);
      const response = await usersService.updateProfile(data);
      
      if (response.success) {
        setUser(prev => ({ ...prev, ...response.data }));
        return { success: true, data: response.data };
      }
      
      return { success: false, error: 'Update fehlgeschlagen' };
    } catch (err) {
      const message = err.message || 'Update fehlgeschlagen';
      setError(message);
      return { success: false, error: message };
    }
  };

  // Update theme preference ('auto' | 'light' | 'dark')
  const updateThemePreference = async (preference) => {
    const safe = VALID_PREFERENCES.includes(preference) ? preference : 'auto';
    setThemePreference(safe);
    setUser(prev => prev ? { ...prev, theme_preference: safe } : prev);
    try {
      await usersService.updateProfile({ theme_preference: safe });
    } catch (err) {
      console.error('Theme preference save error:', err);
    }
  };

  // Update user role
  const updateRole = async (role) => {
    try {
      const response = await usersService.updateRole(role);
      
      if (response.success) {
        setUser(prev => ({ ...prev, role: response.data.role }));
        return { success: true };
      }
      
      return { success: false, error: 'Update fehlgeschlagen' };
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  // Refresh user data from backend
  const refreshUser = async () => {
    try {
      const profile = await fetchUserProfile();
      if (profile) {
        setUser(prev => ({
          ...prev,
          ...profile,
          firebaseUid: prev?.firebaseUid
        }));
        return { success: true };
      }
      return { success: false };
    } catch (err) {
      console.error('Refresh user error:', err);
      return { success: false, error: err.message };
    }
  };

  // Check if user logged in with email/password (not OAuth).
  // Liest bewusst das Ref und nicht auth.currentUser — synchron aufrufbar,
  // ohne Firebase zu laden.
  const isPasswordUser = () => {
    const firebaseUser = firebaseUserRef.current;
    if (!firebaseUser) return false;
    return firebaseUser.providerData.some(
      provider => provider.providerId === 'password'
    );
  };

  // Send password reset email (best-practice: no error disclosure to caller)
  const sendPasswordReset = async (email) => {
    try {
      const actionCodeSettings = {
        // Nach erfolgreichem Reset zurück zur App leiten
        url: `${window.location.origin}/login`,
        handleCodeInApp: false
      };
      const [authInstance, { sendPasswordResetEmail }] = await Promise.all([
        getAuth(),
        getAuthModule(),
      ]);
      await sendPasswordResetEmail(authInstance, email, actionCodeSettings);
      return { success: true };
    } catch (err) {
      // Fehler intern loggen, aber nach außen immer Erfolg signalisieren
      console.error('Password reset error:', err.code);
      return { success: true };
    }
  };

  // Confirm password reset with oobCode from email link
  const confirmPasswordReset = async (oobCode, newPassword) => {
    try {
      const [authInstance, authModule] = await Promise.all([getAuth(), getAuthModule()]);
      await authModule.verifyPasswordResetCode(authInstance, oobCode);
      await authModule.confirmPasswordReset(authInstance, oobCode, newPassword);
      return { success: true };
    } catch (err) {
      const message = getErrorMessage(err.code);
      return { success: false, error: message };
    }
  };

  // Change password (requires current password for re-authentication)
  const changePassword = async (currentPassword, newPassword) => {
    try {
      setError(null);
      const authModule = await getAuthModule();
      const firebaseUser = firebaseUserRef.current;

      if (!firebaseUser) {
        return { success: false, error: 'Nicht angemeldet' };
      }

      if (!isPasswordUser()) {
        return { success: false, error: 'Passwort kann nur für E-Mail-Accounts geändert werden' };
      }

      // Re-authenticate user first
      const credential = authModule.EmailAuthProvider.credential(
        firebaseUser.email,
        currentPassword
      );
      await authModule.reauthenticateWithCredential(firebaseUser, credential);

      // Update password
      await authModule.updatePassword(firebaseUser, newPassword);

      return { success: true };
    } catch (err) {
      const message = getErrorMessage(err.code);
      return { success: false, error: message };
    }
  };

  // Delete account (requires password for re-authentication)
  const deleteAccount = async (password) => {
    try {
      setError(null);
      const authModule = await getAuthModule();
      const firebaseUser = firebaseUserRef.current;

      if (!firebaseUser) {
        return { success: false, error: 'Nicht angemeldet' };
      }

      // Re-authenticate user first (required for sensitive operations)
      if (isPasswordUser() && password) {
        const credential = authModule.EmailAuthProvider.credential(
          firebaseUser.email,
          password
        );
        await authModule.reauthenticateWithCredential(firebaseUser, credential);
      }

      // Delete user data from backend first
      try {
        await usersService.deleteAccount();
      } catch (backendErr) {
        console.error('Backend delete error:', backendErr);
        // Continue with Firebase deletion even if backend fails
      }

      // Delete Firebase account
      await authModule.deleteUser(firebaseUser);
      clearSessionFlag();

      setUser(null);
      return { success: true };
    } catch (err) {
      console.error('Delete account error:', err);
      const message = getErrorMessage(err.code);
      return { success: false, error: message };
    }
  };

  const value = {
    user,
    loading,
    error,
    isAuthenticated: !!user && !!user.id,
    isNewUser: user?.isNewUser || false,
    isPasswordUser,
    login,
    register,
    loginWithGoogle,
    logout,
    updateProfile,
    updateThemePreference,
    updateRole,
    refreshUser,
    changePassword,
    sendPasswordReset,
    confirmPasswordReset,
    deleteAccount,
    clearError: () => setError(null)
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

/**
 * Custom hook to use auth context
 */
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

/**
 * Redirect-Flow nur in der installierten PWA (Standalone-Mode) verwenden:
 * Dort ist window.opener null und signInWithPopup liefert das Ergebnis nicht
 * zurück. Im normalen Mobile-Browser-Tab dagegen scheitert signInWithRedirect
 * regelmäßig an ITP/Storage-Partitioning (sessionStorage der Firebase-Auth-
 * Handler-Domain ist nach Rückkehr nicht mehr lesbar) — User landet endlos
 * auf /login zurück. Popup funktioniert dort und ist die robustere Wahl.
 */
function shouldUseRedirectFlow() {
  if (typeof window === 'undefined') return false;

  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
}

/**
 * Get user-friendly error message
 */
function getErrorMessage(code) {
  const messages = {
    'auth/user-not-found': 'Kein Account mit dieser E-Mail gefunden',
    'auth/wrong-password': 'Falsches Passwort',
    'auth/email-already-in-use': 'Diese E-Mail wird bereits verwendet',
    'auth/weak-password': 'Passwort muss mindestens 6 Zeichen haben',
    'auth/invalid-email': 'Ungültige E-Mail-Adresse',
    'auth/popup-closed-by-user': 'Anmeldung abgebrochen',
    'auth/network-request-failed': 'Netzwerkfehler - prüfe deine Verbindung',
    'auth/too-many-requests': 'Zu viele Versuche - bitte warte kurz',
    'auth/invalid-credential': 'Ungültige Anmeldedaten',
    'auth/operation-not-allowed': 'Diese Anmeldemethode ist nicht aktiviert',
    'auth/account-exists-with-different-credential': 'Ein Konto mit dieser E-Mail existiert bereits',
    'auth/requires-recent-login': 'Bitte melde dich erneut an',
    'auth/expired-action-code': 'Der Link ist abgelaufen – bitte fordere einen neuen an',
    'auth/invalid-action-code': 'Der Link ist ungültig oder wurde bereits verwendet'
  };
  
  if (messages[code]) return messages[code];

  // Unbekannter Code: mit anzeigen. „Ein Fehler ist aufgetreten" allein war in
  // Support-Fällen wertlos — niemand konnte sagen, WAS schiefging.
  return code
    ? `Ein Fehler ist aufgetreten (${code})`
    : 'Ein Fehler ist aufgetreten';
}

/**
 * Auth-Fehler in die Konsole UND an /api/v1/errors melden.
 * Ohne den Server-Log bleibt bei Fehlern auf fremden Geräten (iOS Safari) nur
 * Raten übrig — der Firebase-Fehlercode ist genau die Information, die fehlt.
 */
function logAuthError(context, err) {
  const code = err?.code || 'unknown';
  console.error(`Auth error [${context}]:`, code, err);
  reportError({
    level: 'error',
    message: `Auth ${context}: ${code}`,
    stack: err?.stack || null,
    path: typeof window !== 'undefined' ? window.location.pathname : null,
    context: { type: 'auth', step: context, code, message: err?.message || null },
  });
}

export default AuthContext;
